
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

export interface AuthUser {
  id: string;
  email: string;
  type: string;
  name: string;
  companyId?: string;
}

export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchUserData = useCallback(async (email: string) => {
    try {
      console.log('Fetching user data for:', email);
      
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (userError) {
        console.error('Error fetching user data:', userError);
        throw userError;
      }

      if (!userData) {
        console.error('No user data found');
        throw new Error('Usuário não encontrado');
      }

      console.log('User data fetched:', userData);

      return {
        id: userData.id,
        email: userData.email,
        type: userData.type,
        name: userData.name,
        companyId: userData.company_id,
      };
    } catch (error) {
      console.error('Error in fetchUserData:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    console.log("Auth: Iniciando verificação de sessão");

    const getUser = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Error getting session:', sessionError);
          throw sessionError;
        }

        if (session?.user?.email && mounted) {
          console.log("Auth: Sessão encontrada para:", session.user.email);
          const userData = await fetchUserData(session.user.email);
          
          if (mounted) {
            console.log("Auth: Definindo dados do usuário:", userData);
            setCurrentUser(userData);
            setError(null);
            
            // Store company ID in localStorage with user-specific key for isolation
            if (userData.companyId) {
              const userSpecificKey = `companyId_${session.user.id}`;
              localStorage.setItem(userSpecificKey, userData.companyId);
              localStorage.setItem('currentCompanyId', userData.companyId);
              console.log('Company ID saved with user-specific key:', userSpecificKey, userData.companyId);
            }
          }
        } else if (mounted) {
          console.log("Auth: Nenhuma sessão encontrada");
          setCurrentUser(null);
          localStorage.removeItem('currentCompanyId');
        }
      } catch (error: any) {
        console.error('Error in getUser:', error);
        if (mounted) {
          setError('Erro ao buscar dados do usuário.');
          setCurrentUser(null);
          localStorage.removeItem('currentCompanyId');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth: Estado de autenticação alterado:", event);
      
      if (!mounted) return;

      if (event === 'SIGNED_IN' && session?.user?.email) {
        try {
          const userData = await fetchUserData(session.user.email);
          if (mounted) {
            setCurrentUser(userData);
            setError(null);
            
            // Store company ID in localStorage with user-specific key for isolation
            if (userData.companyId) {
              const userSpecificKey = `companyId_${session.user.id}`;
              localStorage.setItem(userSpecificKey, userData.companyId);
              localStorage.setItem('currentCompanyId', userData.companyId);
              console.log('Company ID saved on sign in with user-specific key:', userSpecificKey, userData.companyId);
            }
          }
        } catch (error) {
          console.error('Error handling auth state change:', error);
          if (mounted) {
            setError('Erro ao buscar dados do usuário.');
            setCurrentUser(null);
            localStorage.removeItem('currentCompanyId');
          }
        }
      } else if (event === 'SIGNED_OUT' && mounted) {
        setCurrentUser(null);
        setError(null);
        localStorage.removeItem('currentCompanyId');
        // Also clean up any user-specific keys
        if (session?.user?.id) {
          localStorage.removeItem(`companyId_${session.user.id}`);
        }
        console.log('User signed out, company IDs removed from localStorage');
        navigate('/login');
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, fetchUserData]);

  const signOut = useCallback(async () => {
    try {
      console.log('Signing out user:', currentUser?.email);
      
      // Get session to get user ID for cleaning up storage
      const { data: { session } } = await supabase.auth.getSession();
      
      // Clean up localStorage
      localStorage.removeItem('currentCompanyId');
      if (session?.user?.id) {
        localStorage.removeItem(`companyId_${session.user.id}`);
      }
      
      await supabase.auth.signOut();
      setCurrentUser(null);
      setError(null);
      toast({
        title: "Logout realizado com sucesso",
        description: "Você foi desconectado do sistema.",
      });
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Erro ao fazer logout. Por favor, tente novamente.');
      toast({
        variant: "destructive",
        title: "Erro ao fazer logout",
        description: "Houve um problema ao desconectar. Por favor, tente novamente.",
      });
    }
  }, [toast, navigate, currentUser?.email]);

  return { currentUser, loading, error, signOut };
};
